<template>
  <div class="data-table management-main-list-data-table">
    <slot />
    <div class="management-section-wrapper">
      <v-card-title>
        <span class="management-section-title">
          {{ $tc("CustomFields", 2) }}
        </span>
        <v-spacer />
        <v-text-field
          :value="searchFromParent"
          @input="updateSearchInParent"
          append-icon="search"
          :label="$t('Search')"
          class="data-table-search"
          single-line
          hide-details
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="customFields"
        :search="searchFromParent"
        :loading="loading"
        loading-text=""
        :page.sync="pagination.page"
        :items-per-page.sync="paginationCustomFields.rowsPerPage"
        :items-per-page-text="rowsPerPageText"
        :items-per-page-items="rowsPerPageItems"
        :header-props="{ sortIcon }"
        :footer-props="{
          prevIcon,
          nextIcon,
          'items-per-page-options': rowsPerPageItems,
        }"
        :class="{ 'no-show-permission': !userHasPermissionTo('show') }"
        @pagination="
          setRowsOnLocalStorage(
            dataTableCustomFieldsId,
            paginationCustomFields.rowsPerPage
          )
        "
      >
        <template v-slot:item="props">
          <tr
            :class="{ 'cursor-pointer': userHasPermissionTo('show') }"
            @click="readField(props.item)"
          >
            <td>{{ props.item.code }}</td>

            <td>{{ props.item.label }}</td>

            <td>{{ getObjectData(props.item) }}</td>

            <td>{{ getInputTypeName(props.item.input_type) }}</td>

            <td class="edit-delete-button">
              <app-datatable-action-buttons
                :user-can-edit="canEditField"
                @edit="editField(props.item)"
                :user-can-delete="canDeleteField"
                @delete="askConfirmationToDeleteItem(props.item)"
              />
            </td>
          </tr>
        </template>

        <template v-slot:pageText="props">
          {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
          {{ props.itemsLength }}
        </template>

        <template v-slot:no-results>
          <data-table-no-results :search="searchFromParent" />
        </template>

        <template v-slot:no-data>
          <div v-if="loading" />
          <v-alert v-else class="data-table-no-data" icon="warning">
            {{ $t("NoCustomFields") }}
          </v-alert>
        </template>
      </v-data-table>
    </div>
    <app-dialog-box
      :open="deleteDialog"
      :title="$t('DeleteCustomField')"
      :content="$t('DeleteConfirmation') + ' ' + itemToDelete.label + '?'"
      :main-action-label="$t('No')"
      @mainAction="cancelDelete"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="deleteField"
      :loading="loadingDelete"
    />
    <app-dialog-box
      :open="confirmDeleteDialog"
      :title="$t('DeleteCustomField')"
      :content="$t('CustomFieldInUse')"
      :main-action-label="$t('No')"
      @mainAction="confirmDeleteDialog = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="finalDelete"
      :loading="loadingDelete"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import { materialFamilyMixin } from "@/mixins/management/materialFamilyMixin";
import { customFieldsMixin } from "@/mixins/customFieldsMixin";
import DataTableNoResults from "../../../_Atoms/DataTableNoResults";
import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import ManagementButton from "@/components/Management/ManagementButton";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";

export default {
  name: "CustomFieldsTable",

  components: {
    AppDatatableActionButtons,
    DataTableNoResults,
    ManagementButton,
    AppDialogBox,
  },

  mixins: [managementDatatableMixin, materialFamilyMixin, customFieldsMixin],

  data() {
    return {
      /**
       * The permission name for the different actions the user can perform
       */
      permissionName: "custom_field",

      dataTableCustomFieldsId: "dataTableCustomFieldsId" ,
      paginationCustomFields: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableCustomFieldsId"),
      },

      /**
       * The existing custom fields
       */
      customFields: [],
      loading: true,
      loadingDelete: false,
      confirmDeleteDialog: false,
    };
  },

  computed: {
    /**
     * Returns the headers of the table
     * @return {Array} - an array containing the headers for the table, the text to be displayed, the value to be shown and the width
     */
    mainHeaders() {
      return [
        {
          text: this.$i18n.tc("Code", 1),
          value: "code",
          width: "25%",
        },
        {
          text: this.$i18n.tc("Label", 1),
          value: "label",
          width: "25%",
        },
        {
          text: this.$i18n.tc("Object", 1),
          value: "object",
          width: "20%",
        },
        {
          text: this.$i18n.tc("Type", 1),
          value: "type",
          width: "10%",
        },
      ];
    },

    /**
     * Indicates whether the user can edit a custom field or not
     * @return {boolean}
     */
    canEditField() {
      return this.userHasPermissionTo("update");
    },

    /**
     * Indicates whether the user can delete a custom field or not
     * @return {boolean}
     */
    canDeleteField() {
      return this.userHasPermissionTo("destroy");
    },

    userCanEditOrRemoveItems() {
      return true;
    },
  },

  mounted() {
    this.getCustomFields();
  },

  methods: {
    ...mapActions(["fetchCustomFields", "deleteCustomFieldByUuid"]),

    /**
     * Gets the custom fields and assigns them to the variable customFields
     */
    async getCustomFields() {
      this.loading = true;
      try {
        let customFields = await this.fetchCustomFields();
        this.customFields = customFields.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Redirects the user to the page of the details of the selected custom field
     * @param item - the item chose by the user
     */
    readField(item) {
      if (this.userHasPermissionTo("show")) {
        this.$router.push({
          name: "custom-field-view",
          params: { uuid: item.uuid },
        });
      }
    },

    /**
     * Redirects the user to the page to edit the selected custom field
     * @param item - the item chose by the user
     */
    editField(item) {
      if (this.userHasPermissionTo("update")) {
        this.$router.push({
          name: "custom-field-edit",
          params: { uuid: item.uuid },
        });
      }
    },

    /**
     * Deletes the selected custom field
     * @param item - the item chose by the user
     */
    async deleteField() {
      try {
        this.loadingDelete = true;
        if (this.userHasPermissionTo("destroy")) {
          await this.deleteCustomFieldByUuid({id: this.itemToDelete.id, ignoreEntry: 'false'});
        }
      } catch (error) {
        if (error.response.status === 403) {
          this.confirmDeleteDialog = true;
          return;
        }
        console.error(error);
        this.$toasted.global.toast_error(error.response.data.data);
      } finally {
        this.resetValues();
      }
    },

    async resetValues(){
      this.deleteDialog = false;
      this.loadingDelete = false;
      await this.getCustomFields();
    },

    async finalDelete(){
      this.confirmDeleteDialog = false;
      await this.deleteCustomFieldByUuid({id: this.itemToDelete.id, ignoreEntry: 'true'});
      this.resetValues();
    },

    getObjectData(customField) {
      let objectTypeName = this.getObjectTypeName(
        customField.object_type,
        customField.table
      );
      let family = customField.object_type
        ? " (" + this.$i18n.tc("Family", 2) + ")"
        : " (" + this.$i18n.tc("Form", 2) + ")";
      return objectTypeName + family;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/SystemSettings/CustomFields/CustomFieldsTable.vue